import {
  Box,
  VStack,
  Text,
  Heading,
  Badge
} from '@chakra-ui/react';
import { memo, useState, useEffect, useRef } from 'react';

const WhyThisWorksInfo = memo(({ whyThisWorks, onPopupStateChange }) => {
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (onPopupStateChange) {
      onPopupStateChange(isHovered);
    }
  }, [isHovered, onPopupStateChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsHovered(false);
      }
    };

    if (isHovered) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isHovered]);

  const formatExplanationContent = () => {
    const sections = [];
    
    if (whyThisWorks && whyThisWorks.summary) {
      sections.push(
        <Text key="summary" fontSize="sm" fontWeight="medium" color="white" mb={2}>
          {whyThisWorks.summary}
        </Text>
      );
    }

    const priorityFields = [
      { key: 'colour_logic', label: 'Color Logic' },
      { key: 'styling_principle', label: 'Style Principle' },
      { key: 'proportion_logic', label: 'Proportion' },
      { key: 'occasion_logic', label: 'Perfect For' },
      { key: 'silhouette_logic', label: 'Silhouette' },
      { key: 'versatility_logic', label: 'Versatility' },
      { key: 'texture_logic', label: 'Fabric' },
      { key: 'trend_relevance', label: 'Trend' }
    ];

    const availableFields = priorityFields.filter(field => 
      whyThisWorks && whyThisWorks[field.key] && whyThisWorks[field.key].trim()
    );

    if (availableFields.length > 0 && sections.length > 0) {
      sections.push(
        <Box 
          key="divider" 
          height="1px" 
          bg="gray.600" 
          my={2} 
          width="100%"
        />
      );
    }

    availableFields.slice(0, 3).forEach(field => {
      sections.push(
        <Box key={field.key} mb={2}>
          <Text fontSize="xs" fontWeight="semibold" color="blue.300" mb={1}>
            {field.label}
          </Text>
          <Text fontSize="xs" color="gray.200">
            {whyThisWorks[field.key]}
          </Text>
        </Box>
      );
    });

    return sections;
  };

  const tooltipContent = (
    <VStack align="start" spacing={2} maxW="280px" p={1}>
      <Heading 
        size="xs" 
        color="blue.300" 
        fontWeight="bold"
        textAlign="center"
        w="100%"
        pb={1}
        borderBottom="1px solid"
        borderColor="gray.600"
      >
        ✨ Why This Works
      </Heading>
      <VStack align="start" spacing={2} w="100%">
        {whyThisWorks && whyThisWorks.summary ? (
          formatExplanationContent()
        ) : (
          <Text fontSize="sm" color="white">
            Styling advice available when provided by our fashion AI.
          </Text>
        )}
      </VStack>
    </VStack>
  );

  return (
    <Box position="relative" ref={containerRef}>
      <Badge
        colorScheme="blue"
        variant="solid"
        size="sm"
        borderRadius="full"
        fontSize="xs"
        fontWeight="medium"
        cursor="pointer"
        style={{
          boxShadow: "0 0.125rem 0.375rem rgba(0, 0, 0, 0.4)",
          backgroundColor: "rgba(0, 0, 0, 0.8)"
        }}
        _hover={{ 
          backgroundColor: "rgba(0, 0, 0, 0.9)"
        }}
        onClick={(e) => { e.stopPropagation(); setIsHovered(!isHovered); }}
      >
        Why this works
      </Badge>
      
      {isHovered && (
        <Box
          position="absolute"
          top="-10px"
          right="0px"
          bg="black"
          color="white"
          shadow="xl"
          borderRadius="lg"
          maxW="240px"
          w="240px"
          p={2}
          fontSize="xs"
          lineHeight="1.3"
          zIndex={1000}
          transform="translateY(-100%)"
        >
          {tooltipContent}
        </Box>
      )}
    </Box>
  );
});

WhyThisWorksInfo.displayName = 'WhyThisWorksInfo';

export default WhyThisWorksInfo;